import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { z } from "zod";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { pushUserAuthenticated } from "@/lib/analytics";
import { SUBSCRIPTION_TIERS, SubscriptionTier } from "@/lib/subscription";
import { signupFormContent } from "@/data/signupFormContent";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SignupConfirmation from "@/components/auth/SignupConfirmation";

const emailSchema = z.string().trim().email("Please enter a valid email address").max(255);
const passwordSchema = z.string().min(6, "Password must be at least 6 characters").max(72);
const nameSchema = z.string().trim().max(100).optional();

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signIn, signUp, resetPassword, isLoading: authLoading } = useAuth();
  
  // Check URL param for initial mode
  const [isSignUp, setIsSignUp] = useState(searchParams.get("mode") === "signup");
  const [isLoading, setIsLoading] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Redirect if already logged in
  useEffect(() => {
    if (user && !authLoading) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Sync isSignUp state with URL search params
  useEffect(() => {
    const mode = searchParams.get("mode");
    setIsSignUp(mode === "signup");
    setErrors({});
  }, [searchParams]);

  const validateForm = (forgotPasswordMode = false) => {
    const newErrors: Record<string, string> = {};

    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) {
      newErrors.email = emailResult.error.errors[0].message;
    }

    if (!forgotPasswordMode) {
      const passwordResult = passwordSchema.safeParse(password);
      if (!passwordResult.success) {
        newErrors.password = passwordResult.error.errors[0].message;
      }

      if (isSignUp) {
        const firstNameResult = nameSchema.safeParse(firstName);
        if (!firstNameResult.success) {
          newErrors.firstName = firstNameResult.error.errors[0].message;
        }
        
        const lastNameResult = nameSchema.safeParse(lastName);
        if (!lastNameResult.success) {
          newErrors.lastName = lastNameResult.error.errors[0].message;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm(true)) return;
    
    setIsLoading(true);

    try {
      const { error } = await resetPassword(email);
      if (error) {
        toast.error(error.message);
      } else {
        setResetEmailSent(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);

    try {
      if (isSignUp) {
        const planParam = searchParams.get("plan");
        const signupPlan = planParam && ['starter', 'pro', 'unlimited'].includes(planParam) 
          ? planParam 
          : 'free';
        const billingParam = searchParams.get("billing");
        const signupBilling = billingParam === 'annual' ? 'annual' : 'monthly';
        const { data, error } = await signUp(email, password, firstName, lastName, signupPlan, signupBilling);
        
        if (error) {
          if (error.message.includes("already registered")) {
            toast.error("This email is already registered. Please sign in instead.");
          } else {
            toast.error(error.message);
          }
        } else if (data?.user?.identities?.length === 0) {
          // Supabase returns a user with empty identities for existing emails
          setErrors({ email: "This email is already registered. Please sign in instead." });
          toast.error("This email is already registered. Please sign in instead.");
        } else {
          setSignupComplete(true);
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            toast.error("Invalid email or password. Please try again.");
          } else {
            toast.error(error.message);
          }
        } else {
          // Push user_authenticated event for GA4
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (authUser) {
            pushUserAuthenticated(authUser.id);
          }
          navigate("/dashboard", { replace: true });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setIsForgotPassword(false);
    setResetEmailSent(false);
    setErrors({});
  };

  const handleBackToSignIn = () => {
    setIsForgotPassword(false);
    setResetEmailSent(false);
    setErrors({});
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background grain">
      <Navbar />
      <div className="flex-1 flex items-center justify-center px-6 py-16 md:py-24">
        <div className="w-full max-w-md">
          {signupComplete ? (
            <>
              <SignupConfirmation 
                email={email}
                plan={(searchParams.get("plan") as 'starter' | 'pro' | 'unlimited') || 'free'}
                billing={searchParams.get("billing") === 'annual' ? 'annual' : 'monthly'}
              />
              {/* Help text */}
              <p className="text-center text-muted-foreground text-xs mt-6">
                By continuing, you agree to our <a href="https://getgreenlight.net/terms-of-service" className="text-link hover:text-link-hover hover:underline">Terms of Use</a> and <a href="https://getgreenlight.net/privacy-policy" className="text-link hover:text-link-hover hover:underline">Privacy Policy</a>.
              </p>
            </>
          ) : (
          <>
          {/* Auth Card */}
          <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
            {isForgotPassword ? (
              // Forgot Password Mode
              resetEmailSent ? (
                // Email sent confirmation
                <>
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                      <Mail className="h-6 w-6 text-accent" />
                    </div>
                  </div>
                  <h1 className="font-display text-2xl font-bold text-foreground text-center mb-2">
                    Check your email
                  </h1>
                  <p className="text-muted-foreground text-center mb-6">
                    We've sent a password reset link to <span className="font-medium text-foreground">{email}</span>
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleBackToSignIn}
                  >
                    Back to sign in
                  </Button>
                </>
              ) : (
                // Forgot password form
                <>
                  <h1 className="font-display text-2xl font-bold text-foreground text-center mb-2">
                    Reset your password
                  </h1>
                  <p className="text-muted-foreground text-center mb-6">
                    Enter your email and we'll send you a link to reset it.
                  </p>

                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          autoComplete="email"
                        />
                      </div>
                      {errors.email && (
                        <p className="text-destructive text-sm">{errors.email}</p>
                      )}
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Send reset link"
                      )}
                    </Button>
                  </form>

                  <div className="mt-6 text-center">
                    <button
                      type="button"
                      onClick={handleBackToSignIn}
                      className="text-link underline font-medium hover:text-link-hover text-sm"
                    >
                      ← Back to sign in
                    </button>
                  </div>
                </>
              )
            ) : (
              // Normal Sign In / Sign Up Mode
              <>
                {/* Step indicator for paid plans */}
                {isSignUp && (() => {
                  const planParam = searchParams.get("plan") as SubscriptionTier | null;
                  const isPaidPlan = planParam && ['starter', 'pro', 'unlimited'].includes(planParam);
                  return isPaidPlan ? (
                    <div className="flex justify-center mb-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-link/10 text-link">
                        {signupFormContent.paid.stepIndicator}
                      </span>
                    </div>
                  ) : null;
                })()}
                
                <h1 className="font-display text-2xl font-bold text-foreground text-center mb-2">
                  {isSignUp ? signupFormContent.free.title : "Welcome back"}
                </h1>
                <p className="text-muted-foreground text-center mb-6">
                  {isSignUp 
                    ? (() => {
                        const planParam = searchParams.get("plan") as SubscriptionTier | null;
                        const isPaidPlan = planParam && ['starter', 'pro', 'unlimited'].includes(planParam);
                        if (isPaidPlan) {
                          const planConfig = SUBSCRIPTION_TIERS[planParam];
                          const billingParam = searchParams.get("billing");
                          const isAnnual = billingParam === 'annual';
                          const displayPrice = isAnnual ? planConfig.annualMonthly : planConfig.monthlyPrice;
                          const billingSuffix = isAnnual ? '/mo billed annually' : '/month';
                          return `You're signing up for the ${planConfig.name} plan at $${displayPrice}${billingSuffix}`;
                        }
                        return signupFormContent.free.subtitle;
                      })()
                    : "Sign in to access your projects"
                  }
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {isSignUp && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First name</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="firstName"
                            type="text"
                            placeholder="John"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                        {errors.firstName && (
                          <p className="text-destructive text-sm">{errors.firstName}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last name</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="lastName"
                            type="text"
                            placeholder="Doe"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                        {errors.lastName && (
                          <p className="text-destructive text-sm">{errors.lastName}</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        autoComplete="email"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-destructive text-sm">{errors.email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      {!isSignUp && (
                        <button
                          type="button"
                          onClick={() => setIsForgotPassword(true)}
                          className="text-sm text-link hover:text-link-hover hover:underline"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10"
                        autoComplete={isSignUp ? "new-password" : "current-password"}
                      />
                    </div>
                    {errors.password && (
                      <p className="text-destructive text-sm">{errors.password}</p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isSignUp ? (
                      "Create account"
                    ) : (
                      "Sign in"
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-foreground/70 text-sm">
                    {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
                    <button
                      type="button"
                      onClick={toggleMode}
                      className="text-link underline font-medium hover:text-link-hover"
                    >
                      {isSignUp ? "Sign in" : "Sign up"}
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Help text */}
          <p className="text-center text-muted-foreground text-xs mt-6">
            By continuing, you agree to our <a href="https://getgreenlight.net/terms-of-service" className="text-link hover:text-link-hover hover:underline">Terms of Use</a> and <a href="https://getgreenlight.net/privacy-policy" className="text-link hover:text-link-hover hover:underline">Privacy Policy</a>.
          </p>
          </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Auth;
